import {
  useCallback, useEffect, useState,
} from 'react';
import {
  Link, NavLink, useLocation, useNavigate,
} from 'react-router-dom';
import axios from 'axios';
import { SERVICE_NAME } from '../constants/AppConstants';
import {
  YOUR_VOYAGES_PAGE_NAME,
  YOUR_VOYAGES_URL,
  LANDING_URL,
  TEMPLATE_PAGE_URL,
  TEMPLATE_PAGE_NAME,
  SIGN_IN_URL,
  YOUR_DETAILS_PAGE_URL,
  YOUR_DETAILS_PAGE_NAME,
  GOV_URL,
  LOGGED_IN_HELP,
  HELP_PAGE_NAME,
} from '../constants/AppUrlConstants';
import { SIGN_OUT_ENDPOINT } from '../constants/AppAPIConstants';
import useUserIsPermitted from '../hooks/useUserIsPermitted';
import Auth from '../utils/Auth';
import '../assets/css/nav.scss';

const navData = [
  {
    id: 'YourVoyages',
    urlStem: YOUR_VOYAGES_URL,
    text: YOUR_VOYAGES_PAGE_NAME,
    active: false,
  },
  {
    id: 'YourDetails',
    urlStem: YOUR_DETAILS_PAGE_URL,
    text: YOUR_DETAILS_PAGE_NAME,
    active: false,
  },
  {
    id: 'Templates',
    urlStem: TEMPLATE_PAGE_URL,
    text: TEMPLATE_PAGE_NAME,
    active: false,
  },
  {
    id: 'Help',
    urlStem: LOGGED_IN_HELP,
    text: HELP_PAGE_NAME,
    active: false,
  },
];

const Nav = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const showNav = useUserIsPermitted();
  const [menuState, setMenuState] = useState(false);
  const [navItems, setNavItems] = useState(navData);

  const menuToggle = (e) => {
    e.preventDefault();
    setMenuState(!menuState);
  };

  const setActivePage = useCallback((url) => {
    const tempArr = [...navData];
    const newArr = tempArr.map((item) => {
      const currentUrl = !url ? item.urlStem : url;
      if (currentUrl === item.urlStem) {
        return ({
          ...item,
          active: true,
        });
      }
      return ({
        ...item,
        active: false,
      });
    });
    document.activeElement.blur();
    setNavItems(newArr);
  }, []);

  const removeFormData = () => {
    sessionStorage.removeItem('formData');
  };

  const handleSignOut = async () => {
    try {
      const response = await axios.post(SIGN_OUT_ENDPOINT, {}, {
        headers: { Authorization: `Bearer ${Auth.retrieveRefreshToken()}` },
      });
      if (response.status === 200) {
        Auth.logout();
        navigate(SIGN_IN_URL);
      }
    } catch (err) {
      Auth.logout();
      navigate(SIGN_IN_URL);
    } finally {
      sessionStorage.removeItem('page'); // removes the page stored so when user signs in it reverts to page 1
    }
  };

  useEffect(() => {
    setActivePage(pathname);
  }, [pathname, setActivePage]);

  return (
    <>
      <div className="govuk-header__logo">
        <Link
          to={GOV_URL}
          className="govuk-header__link govuk-header__link--homepage"
          onClick={() => { removeFormData(); }}
        >
          <span className="govuk-header__logotype">
            <svg
              aria-hidden="true"
              focusable="false"
              className="govuk-header__logotype-crown"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 32 30"
              height="30"
              width="32"
            >
              <path
                fill="currentColor"
                fillRule="evenodd"
                d="M22.6 10.4c-1 .4-2-.1-2.4-1-.4-.9.1-2 1-2.4.9-.4 2 .1 2.4 1s-.1 2-1 2.4m-5.9 6.7c-.9.4-2-.1-2.4-1-.4-.9.1-2 1-2.4.9-.4 2 .1 2.4 1s-.1 2-1 2.4m10.8-3.7c-1 .4-2-.1-2.4-1-.4-.9.1-2 1-2.4.9-.4 2 .1 2.4 1s0 2-1 2.4m3.3 4.8c-1 .4-2-.1-2.4-1-.4-.9.1-2 1-2.4.9-.4 2 .1 2.4 1s-.1 2-1 2.4M17 4.7l2.3 1.2V2.5l-2.3.7-.2-.2.9-3h-3.4l.9 3-.2.2c-.1.1-2.3-.7-2.3-.7v3.4L15 4.7c.1.1.1.2.2.2l-1.3 4c-.1.2-.1.4-.1.6 0 1.1.8 2 1.9 2.2h.7c1-.2 1.9-1.1 1.9-2.1 0-.2 0-.4-.1-.6l-1.3-4c-.1-.2 0-.2.1-.3m-7.6 5.7c.9.4 2-.1 2.4-1 .4-.9-.1-2-1-2.4-.9-.4-2 .1-2.4 1s0 2 1 2.4m-5 3c.9.4 2-.1 2.4-1 .4-.9-.1-2-1-2.4-.9-.4-2 .1-2.4 1s.1 2 1 2.4m-3.2 4.8c.9.4 2-.1 2.4-1 .4-.9-.1-2-1-2.4-.9-.4-2 .1-2.4 1s0 2 1 2.4m14.8 11c4.4 0 8.6.3 12.3.8 1.1-4.5 2.4-7 3.7-8.8l-2.5-.9c.2 1.3.3 1.9 0 2.7-.4-.4-.8-1.1-1.1-2.3l-1.2 4c.7-.5 1.3-.8 2-.9-1.1 2.5-2.6 3.1-3.5 3-1.1-.2-1.7-1.2-1.5-2.1.3-1.2 1.5-1.5 2.1-.1 1.1-2.3-.8-3-2-2.3 1.9-1.9 2.1-3.5.6-5.6-2.1 1.6-2.1 3.2-1.2 5.5-1.2-1.4-3.2-.6-2.5 1.6.9-1.4 2.1-.5 1.9.8-.2 1.1-1.7 2.1-3.5 1.9-2.7-.2-2.9-2.1-2.9-3.6.7-.1 1.9.5 2.9 1.9l.4-4.3c-1.1 1.1-2.1 1.4-3.2 1.4.4-1.2 2.1-3 2.1-3h-5.4s1.7 1.9 2.1 3c-1.1 0-2.1-.2-3.2-1.4l.4 4.3c1-1.4 2.2-2 2.9-1.9-.1 1.5-.2 3.4-2.9 3.6-1.9.2-3.4-.8-3.5-1.9-.2-1.3 1-2.2 1.9-.8.7-2.3-1.2-3-2.5-1.6.9-2.2.9-3.9-1.2-5.5-1.5 2-1.3 3.7.6 5.6-1.2-.7-3.1 0-2 2.3.6-1.4 1.8-1.1 2.1.1.2.9-.3 1.9-1.5 2.1-.9.2-2.4-.5-3.5-3 .6 0 1.2.3 2 .9l-1.2-4c-.3 1.1-.7 1.9-1.1 2.3-.3-.8-.2-1.4 0-2.7l-2.9.9C1.3 23 2.6 25.5 3.7 30c3.7-.5 7.9-.8 12.3-.8"
              />
            </svg>
            <span className="govuk-header__logotype-text">
              GOV.UK
            </span>
          </span>
        </Link>
      </div>
      <div className="govuk-header__content">
        <Link
          to={LANDING_URL}
          className="govuk-header__link govuk-header__service-name"
          data-testid="serviceName"
          onClick={() => { setActivePage(LANDING_URL); removeFormData(); }}
        >
          {SERVICE_NAME}
        </Link>

        {showNav && (
          <nav aria-label="Menu" className="govuk-header__navigation" id="menu">
            <button
              type="button"
              onClick={(e) => menuToggle(e)}
              className={
                menuState === false
                  ? 'govuk-header__menu-button govuk-js-header-toggle'
                  : 'govuk-header__menu-button govuk-js-header-toggle govuk-header__menu-button--open'
              }
              aria-controls="navigation"
              aria-label="Show or hide navigation menu"
              aria-expanded={menuState}
            >
              Menu
            </button>

            <ul
              id="navigation"
              className={
                menuState === false
                  ? 'govuk-header__navigation-list'
                  : 'govuk-header__navigation-list govuk-header__navigation-list--open'
              }
              aria-label="Top Level Navigation"
              hidden={menuState}
            >
              {navItems.map((item) => (
                <li
                  key={item.id}
                  className={item.active ? 'govuk-header__navigation-item govuk-header__navigation-item--active' : 'govuk-header__navigation-item'}
                  data-testid={`listitem-${item.id}`}
                >
                  <Link
                    to={item.urlStem}
                    className="govuk-header__link"
                    onClick={() => { setActivePage(item.urlStem); removeFormData(); }}
                  >
                    {item.text}
                  </Link>
                </li>
              ))}
              <li className="govuk-header__navigation-item float">
                <NavLink to={YOUR_VOYAGES_URL} className="govuk-header__link" onClick={handleSignOut}>Sign out</NavLink>
                {/* Link tag cannot be used as we do not have a signout route */}
              </li>
            </ul>
          </nav>
        )}
      </div>
    </>
  );
};

export default Nav;
